import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress, createFilterOptions } from '@mui/material';
import { apiClient } from '../../helper/api';

/** One selectable SLIP+ coverage code, as returned by
 *  `GET /AutomaticFiling/getCoverageCodes?state=<postalAbbv>` → `{ data: [{ code, category, description }] }`. */
export interface CoverageCodeOption {
    code: string;
    category: string;
    description: string;
}

interface CoverageCodePickerProps {
    /** Bearer token — same auth pattern the rest of BatchFilingReview uses. */
    token?: string;
    /** State the batch is being filed in — passed straight through to the endpoint (postal abbreviation). */
    state: string;
    /** Currently resolved coverage code for the row (e.g. "1000"). */
    value: string;
    /** Fires with the picked code when the filer changes it. */
    onChange: (code: string) => void;
    label?: string;
    /** Render in the error state (required-but-missing). */
    missing?: boolean;
}

// Search matches EITHER the code OR the description (createFilterOptions stringifies both via getOptionLabel).
const filterOptions = createFilterOptions<CoverageCodeOption>({
    stringify: (o) => `${o.code} ${o.description} ${o.category}`,
});

/** Searchable coverage-code picker for the batch expand panel's Computed group — the one editable
 *  computed field. Loads valid codes for the selected state and lets the filer pick by code OR
 *  description rather than free-typing a value that has to be a legitimate SLIP+ code. */
export function CoverageCodePicker({ token, state, value, onChange, label = 'SLIP+ coverage code', missing }: CoverageCodePickerProps) {
    const [options, setOptions] = useState<CoverageCodeOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token || !state) return;
        let cancelled = false;
        setLoading(true);
        const query = new URLSearchParams({ state });
        apiClient(`/AutomaticFiling/getCoverageCodes?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res: any) => {
            if (cancelled) return;
            const rows = Array.isArray(res?.data) ? res.data : [];
            setOptions(rows);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [token, state]);

    // Controlled value: match the row's code to a loaded option; if the code isn't in the list yet
    // (options still loading, or a legacy code), surface it as a synthetic option so it still shows.
    const selected = useMemo<CoverageCodeOption | null>(() => {
        if (!value) return null;
        const match = options.find((o) => o.code === value);
        if (match) return match;
        return { code: value, category: '', description: '' };
    }, [options, value]);

    return (
        <Autocomplete<CoverageCodeOption, false, false, false>
            options={options}
            loading={loading}
            value={selected}
            // Match the editable boxes in the Extracted group: no extra top margin, 6px vertical input
            // padding (same box height), so this control lines up on the same baseline as the read-only
            // "State allocation" beside it and the two Computed items read as one even row.
            sx={{
                mt: 0,
                '& .MuiAutocomplete-inputRoot': { paddingTop: '0 !important', paddingBottom: '0 !important' },
                '& .MuiAutocomplete-input': { paddingTop: '6px !important', paddingBottom: '6px !important', paddingLeft: '9px !important', fontSize: '14px' },
            }}
            filterOptions={filterOptions}
            onChange={(_e, next) => onChange(next?.code ?? '')}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            getOptionLabel={(o) => (o.description ? `${o.code} — ${o.description}` : o.code)}
            renderOption={(props, o) => (
                <Box component="li" {...props} key={o.code} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.25, py: 0.75 }}>
                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#132D3D' }}>
                        {o.code}{o.description ? ` — ${o.description}` : ''}
                    </Typography>
                    {o.category && (
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>{o.category}</Typography>
                    )}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    // No floating label — the field item already renders the label above (avoids a doubled
                    // "SLIP+ coverage code"). Placeholder-only, with an aria-label for accessibility.
                    placeholder={value ? undefined : (missing ? 'Select a coverage code' : 'Search code or description')}
                    error={Boolean(missing) && !value}
                    helperText={missing && !value ? 'Required — resolve upstream or pick a code' : undefined}
                    FormHelperTextProps={{ sx: { fontSize: '11px', color: '#c0392b', ml: 0 } }}
                    sx={{
                        '& .MuiInputBase-input': { fontSize: '14px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '6px' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: (Boolean(missing) && !value) ? '#d32f2f' : '#2099d2' },
                    }}
                    inputProps={{ ...params.inputProps, 'aria-label': label }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

export default CoverageCodePicker;
